import { logger, createLogger } from '@/utils/logger'
import { IApiError } from '@/types'

const fileLogger = createLogger('networkErrorHandler')

export interface NetworkErrorInfo {
  isNetworkError: boolean
  isTimeout: boolean
  isOffline: boolean
  isSlowConnection: boolean
  shouldRetry: boolean
  retryDelay: number
  userMessage: string
}

export class NetworkError extends Error {
  public readonly isNetworkError = true
  public readonly isTimeout: boolean
  public readonly isOffline: boolean
  public readonly isSlowConnection: boolean
  public readonly shouldRetry: boolean
  public readonly retryDelay: number
  public readonly userMessage: string
  public readonly originalError: any

  constructor(
    message: string,
    originalError: any,
    info: NetworkErrorInfo
  ) {
    super(message)
    this.name = 'NetworkError'
    this.originalError = originalError
    this.isTimeout = info.isTimeout
    this.isOffline = info.isOffline
    this.isSlowConnection = info.isSlowConnection
    this.shouldRetry = info.shouldRetry
    this.retryDelay = info.retryDelay
    this.userMessage = info.userMessage
  }
}

export function analyzeNetworkError(error: any): NetworkErrorInfo {
  const isOffline = !navigator.onLine
  const isTimeout = error.code === 'ECONNABORTED' || 
                   error.message?.includes('timeout') ||
                   error.message?.includes('TIMEOUT') ||
                   error.name === 'TimeoutError'
  
  const isNetworkError = isOffline || 
                        isTimeout ||
                        error.code === 'ECONNREFUSED' ||
                        error.code === 'ENOTFOUND' ||
                        error.code === 'NETWORK_ERROR' ||
                        error.message?.includes('Network Error') ||
                        error.message?.includes('Failed to fetch') ||
                        error.message?.includes('ERR_INTERNET_DISCONNECTED') ||
                        error.message?.includes('ERR_NETWORK_CHANGED') ||
                        error.message?.includes('ERR_CONNECTION_REFUSED') ||
                        error.message?.includes('ERR_CONNECTION_TIMED_OUT') ||
                        error.message?.includes('ERR_NAME_NOT_RESOLVED')

  let userMessage = 'Something went wrong. Please try again.'
  let shouldRetry = false
  let retryDelay = 1000

  if (isOffline) {
    userMessage = 'You appear to be offline. Please check your internet connection and try again.'
    shouldRetry = true
    retryDelay = 5000 // Wait 5 seconds before retrying when offline
  } else if (isTimeout) {
    userMessage = 'The request is taking longer than expected. This might be due to a slow connection.'
    shouldRetry = true
    retryDelay = 2000 // Wait 2 seconds before retrying on timeout
  } else if (isNetworkError) {
    userMessage = 'Network connection issue. Please check your internet connection and try again.'
    shouldRetry = true
    retryDelay = 3000 // Wait 3 seconds before retrying on network error
  }

  return {
    isNetworkError,
    isTimeout,
    isOffline,
    isSlowConnection: false, // This would be determined by the network status composable
    shouldRetry,
    retryDelay,
    userMessage
  }
}

export function createNetworkError(error: any): NetworkError {
  const info = analyzeNetworkError(error)
  const message = info.isNetworkError ? info.userMessage : error.message || 'Network error occurred'
  
  fileLogger.error('Network error detected:', {
    originalError: error,
    info,
    message
  })
  
  return new NetworkError(message, error, info)
}

export function isNetworkError(error: any): error is NetworkError {
  return error instanceof NetworkError || error?.isNetworkError === true
}

export function shouldRetryRequest(error: any, retryCount: number = 0): boolean {
  if (!isNetworkError(error)) return false
  if (retryCount >= 3) return false // Max 3 retries
  
  // For NetworkError instances, use the shouldRetry property directly
  if (error instanceof NetworkError) {
    return error.shouldRetry
  }
  
  const info = analyzeNetworkError(error)
  return info.shouldRetry
}

export function getRetryDelay(error: any, retryCount: number = 0): number {
  if (!isNetworkError(error)) return 0
  
  // For NetworkError instances, use the retryDelay property directly
  if (error instanceof NetworkError) {
    // Exponential backoff: base delay * 2^retryCount
    return Math.min(error.retryDelay * Math.pow(2, retryCount), 30000) // Max 30 seconds
  }
  
  const info = analyzeNetworkError(error)
  // Exponential backoff: base delay * 2^retryCount
  return Math.min(info.retryDelay * Math.pow(2, retryCount), 30000) // Max 30 seconds
}
